'use client';
import React from 'react';
import {
  Box,
  Typography,
  IconButton,
  Alert,
  Container,
  Button,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useLocale, useTranslations } from 'next-intl';
import Image from 'next/image';
// import styles from './index.module.scss';
import { useRouter } from 'next/navigation';

interface EmptyPlaceholderProps {
  message?: string;
  iconName?: string;
  variant?: string;
  imageSrc?: string;
  width?: number;
  height?: number;
  title?: string;
}

const PlaceholderContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(6),
  backgroundColor: theme.palette.background.paper,
  borderRadius: theme.shape.borderRadius,
  boxShadow: theme.shadows[2],
  textAlign: 'center',
}));

const PlaceholderIcon = styled(IconButton)(({ theme }) => ({
  fontSize: '64px',
  marginBottom: theme.spacing(2),
  color: theme.palette.text.secondary,
}));

const Empty: React.FC<EmptyPlaceholderProps> = ({
  variant = '',
  imageSrc = '',
  width = 120,
  height = 120,
  title = 'No data',
  message = 'No data available',
}) => {
  const t = useTranslations('Empty');
  const router = useRouter();
  const locale = useLocale();

  if (variant === 'table') {
    return (
      <Container sx={{ textAlign: 'center', py: 9 }}>
        <Box display={'flex'} justifyContent={'center'}>
          <Image
            width={width}
            height={height}
            src={imageSrc}
            alt="empty"
            style={{ marginBottom: 2 }}
          />
        </Box>
        <Typography color="#1F2937" fontSize={20} fontWeight={700}>
          {t(title)}
        </Typography>
        <Typography color="#1F2937" fontSize={16} fontWeight={400}>
          {t(message)}
        </Typography>
      </Container>
    );
  } else if (variant === 'page') {
    return (
      <Box
        sx={{
          display: 'flex',
          background: "url('/images/nodata/gradient_blue_bg.png')",
          'background-size': 'cover',
          'overflow-x': 'hidden',
          'overflow-y': 'hidden',
          height: '100vh',
        }}
      >
        <Box
          sx={{
            flex: '1',
            'background-image': "url('/images/nodata/404bg.png')",
            'background-size': 'cover',
            'background-position': 'center',
            'background-repeat': 'no-repeat',
            height: '100vh',
          }}
        >
          <Container
            disableGutters
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              justifyContent: 'center',
              height: '100vh',
              textAlign: 'center',
              gap: 3,
            }}
          >
            <Typography
              fontSize={48}
              fontWeight={700}
              color={'#1F2937'}
              lineHeight={'normal'}
            >
              {t('Page not found')}
            </Typography>
            <Typography
              fontSize={24}
              fontWeight={400}
              color={'#6B7280'}
              lineHeight={1.4}
            >
              {t('We cant find')}
            </Typography>
            <Button
              variant="contained"
              color="primary"
              sx={{
                '--variant-containedBg': '#2563EB',
                fontSize: 14,
                lineHeight: 1.5,
                textTransform: 'none',
                boxShadow: 'none !important', // 覆盖默认的阴影
                padding: '12px 16px',
                height: '38px',
                fontWeight: 600,
                width: 360,
                borderRadius: 1,
                background: 'var(--variant-containedBg)',
                '&:hover': {
                  '--variant-containedBg': '#1D4ED8',
                  '--variant-textBg': 'rgba(37, 99, 235, 0.04)',
                  '--variant-outlinedBorder': '#2563EB',
                  '--variant-outlinedBg': 'rgba(37, 99, 235, 0.04)',
                },
              }}
              onClick={() => {
                router.push(`/`);
              }}
            >
              {t('Take me home')}
            </Button>
          </Container>
        </Box>
      </Box>
    );
  }

  return (
    <Alert
      variant="outlined"
      severity="info"
      sx={{
        width: '100%',
        border: '1px solid #e5e7eb',
        backgroundColor: '#fff',
        '.MuiAlert-icon': {
          color: '#014361',
        },
      }}
    >
      {t(message)}
    </Alert>
  );
};

const Empty2: React.FC<EmptyPlaceholderProps> = ({
  message = 'No data available',
  iconName = 'description',
}) => {
  const t = useTranslations('Empty');
  return (
    <PlaceholderContainer>
      <PlaceholderIcon>{iconName}</PlaceholderIcon>
      <Typography
        variant="body1"
        sx={{ color: 'text.secondary', fontSize: '18px' }}
      >
        {t(message)}
      </Typography>
    </PlaceholderContainer>
  );
};

export default Empty;

export { Empty2 };
